import { useState, useEffect, useRef } from "react";
import "./QuantityInput.scss";

function QuantityInput({ value = 0, onChange, disabled = false }) {
  const [showKeypad, setShowKeypad] = useState(false);
  const [tempValue, setTempValue] = useState("");
  const containerRef = useRef(null);
  const popupRef = useRef(null);
  // tempValueRef sa aktualizuje synchrónne pri každom setTempValue,
  // čím sa vylúči stale-closure problém v natívnych event listeneroch.
  const tempValueRef = useRef("");
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Smer poslednej zmeny pre krátku animáciu čísla (nové príde zdola pri
  // pridaní, zhora pri ubratí). Odvodené počas renderu, nie v efekte -- pri
  // prvom zobrazení tak nič neposkočí, len pri skutočnej zmene.
  const [predchadzajuca, setPredchadzajuca] = useState(value);
  const [smer, setSmer] = useState(null);
  if (value !== predchadzajuca) {
    setPredchadzajuca(value);
    setSmer(value > predchadzajuca ? "hore" : "dole");
  }

  // Zatvoriť klávesnicu pri kliknutí von
  useEffect(() => {
    if (!showKeypad) return;
    function handleOutsideClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowKeypad(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showKeypad]);

  // Fyzická klávesnica keď je popup otvorený (window, aby fungovala aj bez focusu)
  useEffect(() => {
    if (!showKeypad) return;

    const handleGlobalKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      } else if (e.key === "Escape") {
        setShowKeypad(false);
      } else if (e.key === "Backspace") {
        e.preventDefault();
        handleBackspace();
      } else if (/^[0-9]$/.test(e.key)) {
        handleKeyPress(e.key);
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showKeypad, tempValue]);

  // Scrollovanie popupu do viditeľnej oblasti pri otvorení
  useEffect(() => {
    if (!showKeypad) return;
    const timer = setTimeout(() => {
      popupRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);
    return () => clearTimeout(timer);
  }, [showKeypad]);

  function handleDecrement() {
    onChange(Math.max(0, value - 1));
  }

  function handleIncrement() {
    onChange(value + 1);
  }

  function openKeypad() {
    if (disabled) return;
    const initial = value > 0 ? value.toString() : "";
    tempValueRef.current = initial;
    setTempValue(initial);
    setShowKeypad(true);
  }

  function handleKeyPress(num) {
    // Ref čítame a zapisujeme synchrónne – nie cez updater callback,
    // lebo React 18 ho nevolá okamžite (batch) a Enter by čítal starú hodnotu.
    const prev = tempValueRef.current;
    const next = prev === "0" ? num : prev + num;
    const result = next.length <= 3 ? next : prev;
    tempValueRef.current = result;
    setTempValue(result);
  }

  function handleBackspace() {
    const result = tempValueRef.current.slice(0, -1);
    tempValueRef.current = result;
    setTempValue(result);
  }

  function handleConfirm() {
    const n = parseInt(tempValueRef.current, 10);
    onChangeRef.current(isNaN(n) || n < 0 ? 0 : n);
    setShowKeypad(false);
  }

  function handleValueKeyDown(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      // Ak je klávesnica otvorená, Enter rieši window listener – tu ho ignorujeme,
      // aby openKeypad() neresetoval tempValueRef.current pred handleConfirm().
      if (!showKeypad) {
        openKeypad();
      }
    }
  }

  return (
    <div className="qty-stepper" ref={containerRef}>
      <div className="qty-stepper__ctrl">
        <button
          type="button"
          className="qty-stepper__btn qty-stepper__btn--minus"
          onClick={handleDecrement}
          disabled={disabled || value === 0}
          aria-label="Ubrať"
        >
          −
        </button>

        <div
          className={`qty-stepper__value${disabled ? " qty-stepper__value--disabled" : ""}`}
          onClick={openKeypad}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={handleValueKeyDown}
          aria-label={`Počet: ${value}. Kliknite pre otvorenie klávesnice.`}
        >
          {/* `key` prehrá animáciu pri každej zmene znova. */}
          <span key={value} className={smer ? `qty-stepper__cislo qty-stepper__cislo--${smer}` : "qty-stepper__cislo"}>
            {value || 0}
          </span>
        </div>

        <button
          type="button"
          className="qty-stepper__btn qty-stepper__btn--plus"
          onClick={handleIncrement}
          disabled={disabled}
          aria-label="Pridať"
        >
          +
        </button>
      </div>

      {showKeypad && (
        <div className="qty-stepper__popup" ref={popupRef}>
          <div className="qty-stepper__display">
            {tempValue || "0"}
          </div>

          <div className="qty-stepper__grid">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
              <button
                key={num}
                type="button"
                className="qty-stepper__key"
                onClick={() => handleKeyPress(num.toString())}
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              className="qty-stepper__key qty-stepper__key--backspace"
              onClick={handleBackspace}
              aria-label="Vymazať"
            >
              ⌫
            </button>
            <button
              type="button"
              className="qty-stepper__key"
              onClick={() => handleKeyPress("0")}
            >
              0
            </button>
            <button
              type="button"
              className="qty-stepper__key qty-stepper__key--ok"
              onClick={handleConfirm}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuantityInput;
